import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import {
  PlayerProfileStatisticsFilterComponent
} from '../player-profile-statistics-filter/player-profile-statistics-filter.component';
import {LoadingComponent} from '../../../../../../components/generic/loading/loading.component';
import {MessageComponent} from '../../../../../../components/generic/message/message.component';
import {StatisticFilter} from '../../../../model/api/StatisticFilter';
import {ViewService} from '../../../../../shared/service/view.service';
import {PlayerProfileService} from '../../../../service/player-profile.service';
import {RestErrorHelper} from '../../../../../../helper/rest-error-helper';
import {ErrorType} from '../../../../../../model/error-type';
import {DecimalPipe} from '@angular/common';
import {TableModule} from 'primeng/table';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {AveragePipe} from '../../../../../../pipes/average.pipe';
import {Subscription} from 'rxjs';
import {LayoutHelper} from '../../../../../../helper/layout.helper';
import {DateHelper} from '../../../../../../helper/date.helper';
import {LocaleSettings} from 'primeng/calendar/calendar.interface';
import {PlayerSessionService} from '../../../../service/player-session.service';
import {DurationPipe} from '../../../../../../pipes/duration.pipe';
import {
  PlayerProfileProFeatureComponent
} from '../../../player-profile-register/player-profile-pro-feature/player-profile-pro-feature.component';
import {DOMAIN_VALUE_CD_MATCH_TYPE_EINZEL} from '../../../../../../../assets/domains/domains';

export interface StatisticRowType
{
  header: string;
  label: string;
  values: string[];
  percentage: number[];
  trend: number[];
}

@Component({
  selector: 'app-player-profile-statistics-table',
  standalone: true,
  imports: [
    PlayerProfileStatisticsFilterComponent,
    LoadingComponent,
    MessageComponent,
    TableModule,
    TranslatePipe,
    DecimalPipe,
    PlayerProfileProFeatureComponent
  ],
  templateUrl: './player-profile-statistics-table.component.html',
  styleUrl: './player-profile-statistics-table.component.scss'
})
export class PlayerProfileStatisticsTableComponent implements OnInit, AfterViewInit, OnDestroy
{
  public view = inject(ViewService);
  public playerProfileService = inject(PlayerProfileService);
  private restErrorHelper = inject(RestErrorHelper);
  private translate = inject(TranslateService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private playerSessionService = inject(PlayerSessionService);
  private durationPipe = inject(DurationPipe);

  isLoading: boolean;
  errorMsg: ErrorType;
  isPro: boolean;
  filter: StatisticFilter;
  data: any;
  tableData: StatisticRowType[];
  localeSettings: LocaleSettings;

  tableScrollHeight: string;
  viewSubscription: Subscription;
  labelColWidth: number;
  dataColWidth: number;

  ngOnInit(): void
  {
    this.isPro = this.playerProfileService.playerAuthenticationService.isProVersion();
    this.viewSubscription = this.view.onSizeChanged.subscribe(() => this.onSizeChanged());

    const playerProfile = this.playerSessionService.playerProfileObject;
    this.localeSettings = new DateHelper().getCalendarLocale(playerProfile?.languageCd);
  }

  ngAfterViewInit(): void
  {
    this.onSizeChanged();
  }

  ngOnDestroy(): void
  {
    this.viewSubscription?.unsubscribe();
    console.log('UNSUBSCRIBE');
  }

  private onSizeChanged(): void
  {
    this.labelColWidth = this.view.isMobile ? 100 : 120;
    this.dataColWidth = this.view.isMobile ? 80 : 100;

    const posY = LayoutHelper.getTopFromElement('playerProfileStatisticTableDiv', 50);
    const footerHeight = document.getElementById('frontend_footer')?.offsetHeight || 40;
    console.log('onSizeChanged, Y: ' + posY);
    this.tableScrollHeight = (window.innerHeight - posY - 16 - footerHeight) + 'px';
  }

  public filterChanged(filter: StatisticFilter): void
  {
    this.filter = filter;
    console.log('filterChanged: ', filter);
    this.loadData();
  }

  public loadData(): void
  {
    this.isLoading = true;
    this.errorMsg = undefined;

    this.filter = this.getApiFilter();
    this.playerProfileService.getStatisticsTable(this.filter).subscribe({
      next: data =>
      {
        console.log('StatisticTable erhalten', data);
        this.data = data;
        this.createTableData();
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => this.onSizeChanged());
      },
      error: error =>
      {
        console.log('error', error);
        this.isLoading = false;
        this.errorMsg = this.restErrorHelper.getErrorType(error);
      }
    });
  }

  private getApiFilter(): StatisticFilter
  {
    const filter = {...this.filter};
    if (filter.year === 1)
      filter.yearly = true;
    else if (filter.year === 2)
      filter.quarterly = true;
    else if (filter.year === 3)
      filter.monthly = true;
    if (filter.year < 10)
      filter.year = undefined;
    return filter;
  }

  private createTableData(): void
  {
    const showDartsPerLeg = this.filter?.matchTypeCdList?.includes(DOMAIN_VALUE_CD_MATCH_TYPE_EINZEL);
    console.log('showDartsPerLeg: ' + showDartsPerLeg, this.filter);

    this.tableData = [];
    this.addTableDataRow('MATCHES', 'NUMBER_OF_MATCHES');
    this.addTableDataRow('MATCHES', 'MATCHES_WON');
    this.addTableDataRow('MATCHES', 'W_D_L');

    if (this.isPro)
    {
      // this.addTableDataRow('MATCHES', 'SINGLE_MATCHES');  -> Später, wenn Training dabei ist
      this.addTableDataRow('MATCHES', 'NUMBER_OF_LEGS');
      this.addTableDataRow('MATCHES', 'LEGS_WON');
      this.addTableDataRow('MATCHES', 'NUMBER_OF_THROWS');
      this.addTableDataRow('MATCHES', 'NUMBER_OF_DARTS');
      this.addTableDataRow('MATCHES', 'DURATION');
    }
      // this.addTableDataRow('AVERAGE', '⌀ 1D');
    this.addTableDataRow('AVERAGE', '3D Average');

    if (this.isPro)
    {
      this.addTableDataRow('AVERAGE', '⌀ ' + this.translate.instant('FIRST_9D'));
      this.addTableDataRow('AVERAGE', '60+');
      this.addTableDataRow('AVERAGE', '80+');
      this.addTableDataRow('AVERAGE', '100+');
      this.addTableDataRow('AVERAGE', '140+');
      this.addTableDataRow('AVERAGE', '180');

      this.addTableDataRow('CHECKOUT', 'NUMBER_OF_CHECKOUTS');
      this.addTableDataRow('CHECKOUT', 'MAX_CHECKOUT');
      this.addTableDataRow('CHECKOUT', this.translate.instant('CHECKOUT') + ' 50+');
      this.addTableDataRow('CHECKOUT', this.translate.instant('CHECKOUT') + ' 100+');

      if (showDartsPerLeg)
      {
        this.addTableDataRow(this.translate.instant('DARTS_PER_LEG') + ' (501)', 'LEGS_WON');
        for (let idx = 9; idx <= 30; ++idx)
          this.addTableDataRow(this.translate.instant('DARTS_PER_LEG') + ' (501)', idx + ' Darts');
        this.addTableDataRow(this.translate.instant('DARTS_PER_LEG') + ' (501)', '> 30 Darts');
      }
    }

    if (!this.data?.statisticsTable)
      return;

    let i = 0;
    for (const col of this.data.statisticsTable)
    {
      const lastCol = i === this.data.statisticsTable.length - 1;
      col.colHeader = this.getColHeader(col);

      let index = 0;
      this.addTableDataItem(this.tableData[index++], col.countMatchesOpponent);
      this.addTableDataItemPercent(this.tableData[index++], col.countMatchesOpponentWon, col.countMatchesOpponent);
      this.addTableDataItem(this.tableData[index++], col.countMatchesOpponentWon + '/' + col.countMatchesOpponentDraw + '/' + (col.countMatchesOpponent - col.countMatchesOpponentWon - col.countMatchesOpponentDraw));

      if (this.isPro)
      {
        // this.addTableDataItem(this.tableData[index++], col.countMatchesOpponent - col.countMatches); -> Später, wenn Training dabei ist
        this.addTableDataItem(this.tableData[index++], col.countLegs);
        this.addTableDataItemPercent(this.tableData[index++], col.countLegsWon, col.countLegs);
        this.addTableDataItem(this.tableData[index++], col.countThrows);
        this.addTableDataItem(this.tableData[index++], col.dartsTotal);
        this.addTableDataItem(this.tableData[index++], this.durationPipe.transform(col.duration, false, true, true));

        /*const avg1d = AveragePipe.getAverageStr(col.scoreTotal, col.dartsTotal, 2, true, true);
        this.addTableDataItemTrend(this.tableData[index++], avg1d ? avg1d : '-',
          AveragePipe.getAverage(col.scoreTotal, col.dartsTotal, true),
          lastCol ? undefined : AveragePipe.getAverage(this.data.statisticsTable[i + 1].scoreTotal, this.data.statisticsTable[i + 1].dartsTotal, true));*/
      }

      const avg3d = AveragePipe.getAverageStr(col.scoreTotal, col.dartsTotal, 2, true);
      this.addTableDataItemTrend(this.tableData[index++], avg3d ? avg3d : '-',
        AveragePipe.getAverage(col.scoreTotal, col.dartsTotal, false),
        lastCol ? undefined : AveragePipe.getAverage(this.data.statisticsTable[i + 1].scoreTotal, this.data.statisticsTable[i + 1].dartsTotal, false));

      if (this.isPro)
      {
        const avgf9d = AveragePipe.getAverageStr(col.scoreFirst9, col.dartsFirst9, 2, true);
        this.addTableDataItemTrend(this.tableData[index++], avgf9d ? avgf9d : '-',
          AveragePipe.getAverage(col.scoreFirst9, col.dartsFirst9, false),
          lastCol ? undefined : AveragePipe.getAverage(this.data.statisticsTable[i + 1].scoreFirst9, this.data.statisticsTable[i + 1].dartsFirst9, false));

        this.addTableDataItemPercent(this.tableData[index++], col.count60, col.countThrows);
        this.addTableDataItemPercent(this.tableData[index++], col.count80 + col.count90, col.countThrows);
        this.addTableDataItemPercent(this.tableData[index++], col.count100 + col.count130, col.countThrows);
        this.addTableDataItemPercent(this.tableData[index++], col.count140 + col.count170, col.countThrows);
        this.addTableDataItemPercent(this.tableData[index++], col.count180, col.countThrows);

        this.addTableDataItem(this.tableData[index++], col.countLegsWon);
        this.addTableDataItem(this.tableData[index++], col.checkoutMax);
        this.addTableDataItemPercentTrend(this.tableData[index++], col.checkout50, col.countLegsWon,
          lastCol ? undefined : this.data.statisticsTable[i + 1].checkout50, lastCol ? undefined : this.data.statisticsTable[i + 1].countLegsWon);
        this.addTableDataItemPercentTrend(this.tableData[index++], col.checkout100, col.countLegsWon,
          lastCol ? undefined : this.data.statisticsTable[i + 1].checkout100, lastCol ? undefined : this.data.statisticsTable[i + 1].countLegsWon);

        if (showDartsPerLeg)
        {
          this.addTableDataItem(this.tableData[index++], col.countLegsWon501);
          let otherDartsPerLeg = col.countLegsWon501;
          let j = 0;
          for (const item of col.dartsPerLeg)
          {
            otherDartsPerLeg -= item;
            this.addTableDataItemPercent(this.tableData[index++], item > 0 ? item : '-', col.countLegsWon501);
            // this.addTableDataItemPercentTrend(this.tableData[index++], item, col.countLegsWon501,
            //   lastCol ? undefined : this.data.statisticsTable[i + 1].dartsPerLeg[j], lastCol ? undefined : this.data.statisticsTable[i + 1].countLegsWon501);
            j++;
          }
          this.addTableDataItemPercent(this.tableData[index++], otherDartsPerLeg > 0 ? otherDartsPerLeg : '-', col.countLegsWon501);
        }
      }
      i++;
    }
  }

  private addTableDataItem(tableData: StatisticRowType, value: any): void
  {
    tableData.values.push(value);
    tableData.percentage.push(undefined);
    tableData.trend.push(undefined);
  }

  private addTableDataItemTrend(tableData: StatisticRowType, value: any, valueCurrent: number, valueBefore: number): void
  {
    tableData.values.push(value);
    tableData.percentage.push(undefined);
    if (valueCurrent >= 0 && valueBefore >= 0 && value !== '-')
      tableData.trend.push(valueCurrent > valueBefore ? 1 : (valueBefore > valueCurrent ? -1 : 0));
    else tableData.trend.push(undefined);
  }

  private addTableDataItemPercent(tableData: StatisticRowType, value: any, percentReference: number): void
  {
    tableData.values.push(value);
    if (percentReference > 0)
      tableData.percentage.push(+(value * 100 / percentReference));
    else tableData.percentage.push(undefined);

    tableData.trend.push(undefined);
  }

  private addTableDataItemPercentTrend(tableData: StatisticRowType, value: any, percentReference: number, valueBefore: number, percentReferenceBefore: number): void
  {
    let percentageCurrent: number;
    tableData.values.push(value);
    if (percentReference > 0)
    {
      percentageCurrent = +(value * 100 / percentReference);
      tableData.percentage.push(percentageCurrent);
    }
    else tableData.percentage.push(undefined);

    let percentageBefore: number;
    if (percentReferenceBefore > 0)
      percentageBefore = +(valueBefore * 100 / percentReferenceBefore);

    if (percentageCurrent >= 0 && percentageBefore >= 0 && (percentageCurrent > 0 || percentageBefore > 0))
      tableData.trend.push(percentageCurrent > percentageBefore ? 1 : (percentageBefore > percentageCurrent ? -1 : 0));
    else tableData.trend.push(undefined);
  }

  private addTableDataRow(header: string, label: string): void
  {
    this.tableData.push(
      {
        header,
        label,
        values: [],
        trend: [],
        percentage: []
      }
    );
  }

  private getColHeader(col: any): string
  {
    if (this.filter?.yearly)
      return col.year + '';
    else if (this.filter?.quarterly)
      return col.quarter + '/' + col.year;
    else if (this.filter?.monthly && col.month > 0 && col.month <= 12)
      return this.localeSettings.monthNamesShort[col.month - 1] + ' ' + col.year;

    return 'ALL';
  }
}
